import React from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell as fasBell, faCheck, faEye, faRedo } from '@fortawesome/free-solid-svg-icons';
import { faBell as farBell } from '@fortawesome/free-regular-svg-icons';

import { useNotificationBell } from './useNotificationBell';
import { NotificationBellProps } from './types';
import styles from './NotificationBell.module.css';
import LoadingIndicator from '../LoadingIndicator';

/**
 * 通知パネルをポータルとして表示するコンポーネント
 */
const NotificationPanelPortal: React.FC<{
  children: React.ReactNode;
  isOpen: boolean;
}> = ({ children, isOpen }) => {
  if (!isOpen) return null;
  
  return ReactDOM.createPortal(
    children,
    document.body
  );
};

/**
 * 通知ベルコンポーネント
 * ヘッダーなどに配置し、ユーザーへの通知を表示します
 */
const NotificationBell: React.FC<NotificationBellProps> = ({ isMobile = false, onToggle }) => {
  const {
    isOpen,
    unreadCount,
    notifications,
    isLoading,
    processingNotificationIds,
    panelRef,
    notificationListRef,
    bellIconRef,
    bellPosition,
    toggleNotificationPanel,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMarkAllAsRead,
    handleNavigate,
    formatDate
  } = useNotificationBell({ isMobile, onToggle });

  return (
    <div className={styles['notification-bell-container']}>
      <div className={styles['notification-bell-bell-container']}>
        <div 
          ref={bellIconRef} 
          className={`${styles['notification-bell-bell-icon']} ${unreadCount > 0 ? styles['notification-bell-filled'] : styles['notification-bell-empty']}`}
          onClick={toggleNotificationPanel}
          data-testid="notification-bell-icon"
        >
          <FontAwesomeIcon icon={unreadCount > 0 ? fasBell : farBell} />
        </div>
        {unreadCount > 0 && (
          <div 
            className={styles['notification-bell-notification-count']}
            data-testid="notification-bell-count"
          >
            {unreadCount}
          </div>
        )}
      </div>
      {isOpen && (
        <NotificationPanelPortal isOpen={isOpen}>
          <div 
            className={styles['notification-bell-portal']}
            ref={panelRef}
            style={{
              position: 'fixed',
              top: isMobile ? '70px' : `${bellPosition.viewportTop}px`,
              right: isMobile ? '10px' : `${bellPosition.right}px`,
              left: isMobile ? '10px' : 'auto',
              width: isMobile ? 'auto' : '320px',
              maxWidth: isMobile ? 'calc(100% - 20px)' : '320px',
              maxHeight: 'calc(80vh)',
              zIndex: 10001,
              borderRadius: '8px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => {
              // クリックイベントの伝播を停止
              e.stopPropagation();
            }}
            data-testid="notification-panel"
          >
            <div className={styles['notification-bell-panel']}>
              <div className={styles['notification-bell-header']}>
                <span>通知</span>
                {unreadCount > 0 && (
                  <button 
                    className={styles['notification-bell-mark-all-btn']} 
                    onClick={handleMarkAllAsRead}
                    data-testid="notification-mark-all-read-btn"
                  >
                    すべて既読にする
                  </button>
                )}
              </div>
              
              <div 
                className={styles['notification-bell-list']} 
                ref={notificationListRef}
              >
                {isLoading ? (
                  <LoadingIndicator
                    className={styles['notification-bell-loading']}
                  />
                ) : notifications.length === 0 ? (
                  <div className={styles['notification-bell-empty']}>通知なし</div>
                ) : (
                  <>
                    {notifications.map((notification) => {
                      const isRead = notification.is_read === true;
                      const isProcessing = processingNotificationIds.includes(notification.id);
                      
                      return (
                        <div
                          key={notification.id}
                          className={`${styles['notification-bell-item']} ${!isRead ? styles['notification-bell-item-unread'] : ''} ${isProcessing ? styles['notification-bell-item-processing'] : ''}`}
                          onClick={(e) => {
                            // アクションエリアなら何もしない
                            if (e.target && (e.target as HTMLElement).closest(`.${styles['notification-bell-actions']}`)) {
                              return;
                            }
                            
                            // 処理中なら何もしない
                            if (isProcessing) {
                              return;
                            }
                            
                            // 通常の通知クリックで遷移
                            handleNavigate(notification, e);
                          }}
                          data-testid={`notification-item-${notification.id}`}
                        >
                          <div className={styles['notification-bell-content']}>
                            {notification.message || notification.title}
                          </div>
                          <div className={styles['notification-bell-time']}>
                            {formatDate(notification.created_at)}
                          </div>
                          
                          <div className={styles['notification-bell-actions']}>
                            {isRead ? (
                              <button
                                type="button"
                                className={`${styles['notification-bell-action-button']} ${styles['notification-bell-unread-btn']} ${isProcessing ? styles['notification-bell-action-button-disabled'] : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isProcessing) return;
                                  handleMarkAsUnread(notification, e);
                                }}
                                disabled={isProcessing}
                                title="未読にする"
                                data-testid={`notification-unread-btn-${notification.id}`}
                              >
                                <FontAwesomeIcon icon={faRedo} className={styles['notification-bell-action-icon']} /> 未読
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={`${styles['notification-bell-action-button']} ${styles['notification-bell-read-btn']} ${isProcessing ? styles['notification-bell-action-button-disabled'] : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isProcessing) return;
                                  handleMarkAsRead(notification, e);
                                }}
                                disabled={isProcessing}
                                title="既読にする"
                                data-testid={`notification-read-btn-${notification.id}`}
                              >
                                <FontAwesomeIcon icon={faCheck} className={styles['notification-bell-action-icon']} /> 既読
                              </button>
                            )}
                            
                            <button
                              type="button"
                              className={`${styles['notification-bell-action-button']} ${styles['notification-bell-view-btn']} ${isProcessing ? styles['notification-bell-action-button-disabled'] : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isProcessing) return;
                                handleNavigate(notification, e);
                              }}
                              disabled={isProcessing}
                              title="表示"
                              data-testid={`notification-view-btn-${notification.id}`}
                            >
                              <FontAwesomeIcon icon={faEye} className={styles['notification-bell-action-icon']} /> 表示
                            </button>
                          </div>
                          {isProcessing && (
                            <div style={{ 
                              position: 'absolute', 
                              top: 0, 
                              left: 0, 
                              width: '100%', 
                              height: '100%', 
                              background: 'rgba(255,255,255,0.7)', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              zIndex: 10
                            }}>
                              <span style={{ fontSize: '0.8rem', color: '#666' }}>処理中...</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </NotificationPanelPortal>
      )}
    </div>
  );
};

export default NotificationBell; 
